/* eslint-disable @typescript-eslint/no-explicit-any */
import { StateCreator } from 'zustand';
import { createAppStore } from '@marriott/mi-store-utils';
import { produce } from 'immer';
import { ComponentProps } from '../../organisms/HotelOverview/HotelOverview.types';

// Interface for HotelOverview server data state
export interface HotelOverviewServerDataState {
  // Core property data
  propertyOverviewData: ComponentProps['propertyOverviewData'];
  jsonContent: ComponentProps['jsonContent'];
  GOOGLE_MAP_API_KEY: ComponentProps['GOOGLE_MAP_API_KEY'];
  locationData: ComponentProps['locationData'];
  model: ComponentProps['model'];

  // Additional props that may be passed
  pageProps: ComponentProps['pageProps'];
  overridePropertyCountry: ComponentProps['overridePropertyCountry'];
  overridePropertyCountryText: ComponentProps['overridePropertyCountryText'];
}

// Interface for store actions
export interface HotelOverviewServerDataActions {
  // Initialize the store with server data (following shopStoreInitialization pattern)
  hotelOverviewInitialization: (serverData: {
    propertyOverviewData?: any;
    jsonContent?: any;
    GOOGLE_MAP_API_KEY?: string;
    locationData?: any;
    model?: any;
    pageProps?: any;
    overridePropertyCountry?: boolean;
    overridePropertyCountryText?: string;
  }) => void;

  // Update individual properties
  updateServerDataProperty: (key: keyof HotelOverviewServerDataState, value: any) => void;

  // Bulk update functionality
  updateHotelOverviewServerDataState: (updates: Partial<HotelOverviewServerDataState>) => void;

  // Reset store to initial state
  resetHotelOverviewServerDataStore: () => void;

  // Utility methods for checking state
  isInitialState: () => boolean;
}

// Combined store type
export type HotelOverviewServerDataStore = HotelOverviewServerDataState & HotelOverviewServerDataActions;

// Initial state matching the serverDataProps from HotelOverview component
const initialState: HotelOverviewServerDataState = {
  propertyOverviewData: {},
  jsonContent: {},
  GOOGLE_MAP_API_KEY: '',
  locationData: {},
  model: {},
  pageProps: undefined,
  overridePropertyCountry: undefined,
  overridePropertyCountryText: undefined,
};

// Store creator following existing patterns
const hotelOverviewServerDataStore: StateCreator<HotelOverviewServerDataStore> = (set, get) => {
  return {
    ...initialState,

    // Initialize store with server data (following shopStoreInitialization pattern)
    hotelOverviewInitialization: (serverData: {
      propertyOverviewData?: any;
      jsonContent?: any;
      GOOGLE_MAP_API_KEY?: string;
      locationData?: any;
      model?: any;
      pageProps?: any;
      overridePropertyCountry?: boolean;
      overridePropertyCountryText?: string;
    }) => {
      set(
        produce(draft => {
          // Initialize with server data (not from context)
          if (serverData.propertyOverviewData !== undefined) {
            draft.propertyOverviewData = serverData.propertyOverviewData;
          }
          if (serverData.jsonContent !== undefined) {
            draft.jsonContent = serverData.jsonContent;
          }
          if (serverData.GOOGLE_MAP_API_KEY !== undefined) {
            draft.GOOGLE_MAP_API_KEY = serverData.GOOGLE_MAP_API_KEY;
          }
          if (serverData.locationData !== undefined) {
            draft.locationData = serverData.locationData;
          }
          if (serverData.model !== undefined) {
            draft.model = serverData.model;
          }
          if (serverData.pageProps !== undefined) {
            draft.pageProps = serverData.pageProps;
          }
          if (serverData.overridePropertyCountry !== undefined) {
            draft.overridePropertyCountry = serverData.overridePropertyCountry;
          }
          if (serverData.overridePropertyCountryText !== undefined) {
            draft.overridePropertyCountryText = serverData.overridePropertyCountryText;
          }
        })
      );
    },

    // Update individual properties
    updateServerDataProperty: (key: keyof HotelOverviewServerDataState, value: any) => {
      set(
        produce(draft => {
          draft[key] = value;
        })
      );
    },

    // Bulk update functionality for multiple properties
    updateHotelOverviewServerDataState: (updates: Partial<HotelOverviewServerDataState>) => {
      set(
        produce(draft => {
          Object.keys(updates).forEach(key => {
            const typedKey = key as keyof HotelOverviewServerDataState;
            draft[typedKey] = updates[typedKey];
          });
        })
      );
    },

    // Reset store to initial state
    resetHotelOverviewServerDataStore: () => {
      set({ ...initialState });
    },

    // Utility method to check if store is in initial state
    isInitialState: () => {
      const state = get();
      return (
        Object.keys(state.jsonContent || {}).length === 0 &&
        Object.keys(state.propertyOverviewData || {}).length === 0 &&
        Object.keys(state.locationData || {}).length === 0 &&
        Object.keys(state.model || {}).length === 0 &&
        !state.GOOGLE_MAP_API_KEY
      );
    },
  };
};

// Create the store using the existing createAppStore pattern
export const useHotelOverviewServerDataStore = createAppStore(hotelOverviewServerDataStore, {
  usePersistentStore: false,
});

// Export selector helpers for common access patterns
export const hotelOverviewServerDataSelectors = {
  propertyOverviewData: (state: HotelOverviewServerDataStore) => state.propertyOverviewData,
  jsonContent: (state: HotelOverviewServerDataStore) => state.jsonContent,
  GOOGLE_MAP_API_KEY: (state: HotelOverviewServerDataStore) => state.GOOGLE_MAP_API_KEY,
  locationData: (state: HotelOverviewServerDataStore) => state.locationData,
  model: (state: HotelOverviewServerDataStore) => state.model,
  pageProps: (state: HotelOverviewServerDataStore) => state.pageProps,
  overridePropertyCountry: (state: HotelOverviewServerDataStore) => state.overridePropertyCountry,
  overridePropertyCountryText: (state: HotelOverviewServerDataStore) => state.overridePropertyCountryText,
};
